import React from 'react';
import { Box, Typography, makeStyles, Chip } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: 8,
    gap: 10,
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  badge: {
    height: 21,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.4px',
    borderRadius: 4,
    padding: '3px 8px',
  },
  name: {
    flex: 1,
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  owner: {
    fontSize: 11,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    minWidth: 60,
  },
  time: {
    fontSize: 10,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    minWidth: 40,
    textAlign: 'right',
  },
}));

export type EntityKind =
  | 'API'
  | 'Service'
  | 'Component'
  | 'System'
  | 'Group'
  | 'User'
  | 'Resource'
  | 'Domain'
  | 'Template'
  | 'Location';

type CatalogItemProps = {
  kind: EntityKind;
  name: string;
  owner?: string;
  time?: string;
  href?: string;
  onClick?: () => void;
};

const kindStyles: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  API: {
    bg: 'rgba(240, 163, 28, 0.09)',
    color: '#F0A31C',
    border: '1px solid rgba(240, 163, 28, 0.19)',
  },
  Service: {
    bg: 'rgba(82, 146, 244, 0.09)',
    color: '#5292F4',
    border: '1px solid rgba(82, 146, 244, 0.19)',
  },
  Component: {
    bg: 'rgba(34, 197, 94, 0.09)',
    color: '#22C55E',
    border: '1px solid rgba(34, 197, 94, 0.19)',
  },
  System: {
    bg: 'rgba(168, 85, 247, 0.09)',
    color: '#A855F7',
    border: '1px solid rgba(168, 85, 247, 0.19)',
  },
  Group: {
    bg: 'rgba(236, 72, 153, 0.09)',
    color: '#EC4899',
    border: '1px solid rgba(236, 72, 153, 0.19)',
  },
  User: {
    bg: 'rgba(99, 102, 241, 0.09)',
    color: '#6366F1',
    border: '1px solid rgba(99, 102, 241, 0.19)',
  },
  Resource: {
    bg: 'rgba(20, 184, 166, 0.09)',
    color: '#14B8A6',
    border: '1px solid rgba(20, 184, 166, 0.19)',
  },
  Domain: {
    bg: 'rgba(245, 158, 11, 0.09)',
    color: '#F59E0B',
    border: '1px solid rgba(245, 158, 11, 0.19)',
  },
  Template: {
    bg: 'rgba(107, 107, 122, 0.09)',
    color: '#6B6B7A',
    border: '1px solid rgba(107, 107, 122, 0.19)',
  },
  Location: {
    bg: 'rgba(156, 163, 175, 0.09)',
    color: '#9CA3AF',
    border: '1px solid rgba(156, 163, 175, 0.19)',
  },
};

export const CatalogItem = ({
  kind,
  name,
  owner,
  time,
  href,
  onClick,
}: CatalogItemProps) => {
  const classes = useStyles();
  const style = kindStyles[kind] || kindStyles.Component;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const content = (
    <>
      <Chip
        label={kind}
        size="small"
        className={classes.badge}
        style={{
          backgroundColor: style.bg,
          color: style.color,
          border: style.border,
        }}
      />
      <Typography className={classes.name}>{name}</Typography>
      {owner && <Typography className={classes.owner}>{owner}</Typography>}
      {time && <Typography className={classes.time}>{time}</Typography>}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={classes.listItem}
        onClick={handleClick}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {content}
      </a>
    );
  }

  return (
    <Box className={classes.listItem} onClick={handleClick}>
      {content}
    </Box>
  );
};
